import PropTypes from 'prop-types'
import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Route } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import {
  Sidebar as SidebarComponent,
  MenuItem,
  MenuDivider,
  MenuItemAction,
} from 'components/Sidebar'
import Sortable from 'components/Sortable'
import AddLayout from './AddLayout'
import DeskSidebarItem from './DeskSidebarItem'
import Settings from './Settings'
import KanBoIcon from 'components/KanBoIcon'
import classes from './Sidebar.module.scss'
import { useHistory } from 'react-router'
import styled from 'styled-components'
import Desks from './Desks'
import withLeftSidebar from 'components/Sidebar/withLeftSidebar'
import useLayoutsData from '../hooks/useLayoutsData'
import useUnreadBoardActivities from '../hooks/useUnreadBoardActivities'
import useBoardsBlockerCount from '../hooks/useBoardsBlockerCount'
import { createSelector } from 'reselect'
import sortedLayoutsSelector from '../selectors/sortedLayouts'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { useLocalStorage } from '@rehooks/local-storage'
import RecentBoard from './RecentBoard'
import _ from 'lodash'
import usePrevious from 'hooks/usePrevious'
import useInterval from 'hooks/useInterval'

const BoardCollectionOptions = styled.div`
  display: flex;
`

const ManageBoardCollectionsMenuItem = styled(MenuItem)``

const ManageBoardCollectionsTitle = styled.div`
  color: #666;

  ${ManageBoardCollectionsMenuItem}:hover & {
    color: inherit;
  }
`

const SettingsSection = styled.div`
  margin-top: auto;
  margin-bottom: 5px;
`

export function Sidebar ({
  leftSidebar,

  layouts,
  layoutIds,

  fetchBoardTile,
  requestPositionLayout,
  setIsSidebarVisible,
}) {
  const history = useHistory()

  const layoutMatch = useRouteMatch('/home/:id(\\d+)')

  const [showMoreLayouts, setShowMoreLayouts] = useState(false)

  const [showAddLayout, setShowAddLayout] = useState(false)

  const filteredlayoutsSelector = useMemo(() => createSelector(
    [
      sortedLayoutsSelector,
      state => state['view-home'].layouts.byId,
    ],
    (
      layouts,
      layoutsById,
    ) =>
      [
        ...(showMoreLayouts ? layouts : layouts.slice(0, 5)),
        ...(
          (layoutMatch && layoutsById[layoutMatch.params.id])
            ? [layoutsById[layoutMatch.params.id]]
            : []
        )
      ].filter(layout => !layout.isBusy)
  ), [layoutMatch, showMoreLayouts])

  const filteredLayoutIds = useSelector(useMemo(() => createSelector(
    filteredlayoutsSelector,
    layouts =>
      Array.from(new Set([
        ...layouts.map(layout => layout.id),
        ...(
          layoutMatch
            ? [Number(layoutMatch.params.id)]
            : []
        )
      ]))
  ), [filteredlayoutsSelector, layoutMatch]))

  useLayoutsData(filteredLayoutIds)

  const boardIds = useSelector(useMemo(() => createSelector(
    [
      filteredlayoutsSelector,
      state => state['view-home'].layoutGroupsByKey,
      state => state.kanbo.boardsById,
    ],
    (
      layouts,
      layoutGroupsByKey,
      boardsById,
    ) =>
      Array.from(
        layouts.reduce((set, layout) => {
          layout.layoutGroups.forEach(layoutGroupId => {
            layoutGroupsByKey[`${layout.id}-${layoutGroupId}`].elements.forEach(element => {
              if (
                element.type === 'board' &&
                !element.isBusy &&
                !boardsById[element.boardId]?.isBusy &&
                !boardsById[element.boardId]?.isClosed &&
                !boardsById[element.boardId]?.isLocked &&
                boardsById[element.boardId]?.hasAccess
              ) {
                set.add(element.boardId)
              }
            })
          })

          return set
        }, new Set())
      )
  ), [filteredlayoutsSelector]))

  useUnreadBoardActivities(boardIds)
  useBoardsBlockerCount(boardIds)

  const [recentBoardsCollapsed, setRecentBoardsCollapsed] = useLocalStorage('kanbo:recentBoardsCollapsed', false)

  const boardMatch = useRouteMatch('/board/:boardId(\\d+)')
  const matchBoardId = boardMatch ? Number(boardMatch.params.boardId) : null
  const [recentBoards, setRecentBoards] = useLocalStorage('kanbo:recentBoards', [])
  const prevRecentBoards = usePrevious(recentBoards)
  useEffect(() => {
    if (matchBoardId && !recentBoards.includes(matchBoardId)) {
      setRecentBoards([
        matchBoardId,
        ...recentBoards,
      ].slice(0, 4))
    }
  }, [matchBoardId])
  useEffect(() => {
    const addedBoardIds = _.difference(recentBoards, prevRecentBoards, boardIds)

    if (addedBoardIds.length > 0) {
      addedBoardIds.forEach(boardId => fetchBoardTile({ boardId }))
    }
  }, [recentBoards])
  useInterval(() => {
    const diffBoardIds = _.difference(recentBoards, boardIds)

    if (diffBoardIds.length > 0) {
      diffBoardIds.forEach(boardId => fetchBoardTile({ boardId }))
    }
  }, 60000) // 1 minute

  const toggleShowMoreLayouts = useCallback(() => {
    setShowMoreLayouts(!showMoreLayouts)
  }, [showMoreLayouts])

  const handleDeskPositionChange = useCallback(({ itemId, position }) => {
    requestPositionLayout({
      id: itemId,
      position
    })
  }, [requestPositionLayout])

  const handleBoardsClick = useCallback(() => {
    setIsSidebarVisible(false)

    history.push('/home/boards')
  }, [history, setIsSidebarVisible])

  const handleManageBoardCollectionsClick = useCallback(() => {
    setIsSidebarVisible(false)

    leftSidebar.push(Desks, { position: 'left' })
  }, [leftSidebar, setIsSidebarVisible])

  const handleRecentBoardsCollapseClick = useCallback(() => {
    setRecentBoardsCollapsed(!recentBoardsCollapsed)
  }, [recentBoardsCollapsed])

  const showMoreAvailable = layouts.length > 5

  return (
    <SidebarComponent
      closeOnClickOutside={!showAddLayout}
    >
      <MenuItem
        icon={<KanBoIcon name='kanbo-24-reminder' />}
        title={<FormattedMessage id='RECENT_BOARDS' />}
        actions={
          <MenuItemAction
            icon={<KanBoIcon name={recentBoardsCollapsed ? 'kanbo-16-chevron-left' : 'kanbo-16-chevron-down'} />}
            onClick={handleRecentBoardsCollapseClick}
          />
        }
      />
      {!recentBoardsCollapsed && recentBoards.map(boardId =>
        <RecentBoard
          key={boardId}
          boardId={boardId}
        />
      )}
      <MenuDivider />
      <BoardCollectionOptions>
        <AddLayout
          onToggle={setShowAddLayout}
        />
        <ManageBoardCollectionsMenuItem
          title={
            <ManageBoardCollectionsTitle>
              <KanBoIcon name='kanbo-16-settings' />
            </ManageBoardCollectionsTitle>
          }
          onClick={handleManageBoardCollectionsClick}
        />
      </BoardCollectionOptions>
      {!layouts.length ? (
        <MenuItem
          title={
            <span
              className={classes.noDesksInfo}
            >
              <FormattedMessage id='DESKS_NO_DESKS' />
            </span>
          }
        />
      ) : (
        <Sortable
          vertical
          name='desks'
          component={DeskSidebarItem}
          items={layoutIds}
          onPositionChange={handleDeskPositionChange}
        >
          {({ items, renderItem, connectDropTarget }) =>
            connectDropTarget(
              <div>
                {items.slice(0, 5).map((itemId, index) =>
                  renderItem({
                    key: itemId,
                    id: itemId,
                    index
                  })
                )}
                {showMoreAvailable &&
                  <MenuItem
                    title={
                      <React.Fragment>
                        <KanBoIcon
                          name={showMoreLayouts ? 'kanbo-16-chevron-down' : 'kanbo-16-chevron-right'}
                          className={classes.moreDesksIcon}
                        />
                        <FormattedMessage id='DESKS_MORE' />
                      </React.Fragment>
                    }
                    onClick={toggleShowMoreLayouts}
                  />
                }
                {showMoreLayouts && items.slice(5).map((itemId, index) =>
                  renderItem({
                    key: itemId,
                    id: itemId,
                    index: index + 5
                  })
                )}
              </div>
            )
          }
        </Sortable>
      )}
      <MenuDivider />
      <Route
        exact
        path='/home/boards'
      >
        {({ match }) =>
          <MenuItem
            icon={<KanBoIcon name='kanbo-24-board' />}
            title={<FormattedMessage id='BOARDS' />}
            selected={Boolean(match)}
            onClick={handleBoardsClick}
          />
        }
      </Route>
      <SettingsSection>
        <MenuDivider />
        <MenuItem
          icon={<KanBoIcon name='kanbo-24-settings' />}
          title={<FormattedMessage id='SETTINGS' />}
          view={Settings}
        />
      </SettingsSection>
    </SidebarComponent>
  )
}

Sidebar.propTypes = {
  layouts: PropTypes.array.isRequired,
  layoutIds: PropTypes.array.isRequired,

  requestPositionLayout: PropTypes.func.isRequired,
}

export default withLeftSidebar(Sidebar)
